import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { ApiError, errorMessage } from '@/lib/api';
import { useOnline } from '@/hooks/useOnline';
import { Button } from './Button';
import { cn } from './cn';
import { Icon } from './Icon';

export function EmptyState({ title, description, action, icon = <Icon name="inbox" size={36} /> }: { title: string; description?: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-sand-300 bg-white px-6 py-10 text-center">
      <div className="mb-3 flex text-brand-800" aria-hidden="true">
        {icon}
      </div>
      <p className="font-semibold text-gray-900">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-gray-600">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const offline = error instanceof ApiError && error.isOffline;
  return (
    <div role="alert" className="flex flex-col items-center rounded-2xl border border-[#F3C9C2] bg-[#FBE4E1] px-6 py-8 text-center">
      <div className="mb-2 flex text-[#9B2C1F]" aria-hidden="true">
        <Icon name={offline ? 'offline' : 'alert'} size={32} />
      </div>
      <p className="font-bold text-[#7F2318]">{offline ? 'Vous êtes hors ligne' : 'Impossible de charger'}</p>
      <p className="mt-1 text-sm text-[#7F2318]">{offline ? 'Ce contenu n’a pas encore été consulté avec une connexion.' : errorMessage(error)}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-2xl bg-sand-100', className)} aria-hidden="true" />;
}

export function ListSkeleton({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-3', className)} role="status" aria-label="Chargement">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-24 w-full" />
      ))}
    </div>
  );
}

/**
 * Gère de façon uniforme les états d'une requête : chargement, erreur, vide, hors ligne.
 * Les données en cache restent affichées hors ligne (bandeau discret).
 */
export function QueryState<T>({
  query,
  children,
  loading,
  empty,
  isEmpty,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  loading?: ReactNode;
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
}) {
  const online = useOnline();
  if (query.isPending) return <>{loading ?? <ListSkeleton />}</>;
  if (query.isError && query.data === undefined) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const data = query.data as T;
  if (isEmpty?.(data)) return <>{empty ?? <EmptyState title="Rien à afficher pour le moment" />}</>;
  return (
    <>
      {!online && <p className="mb-2 rounded-xl bg-gold-100 px-3 py-2 text-xs font-semibold text-gold-800">Données enregistrées — mise à jour au retour du réseau.</p>}
      {children(data)}
    </>
  );
}
